import FadeIn from '@/components/motion/fade-in';
import { AUTH_STAGGER, DURATION, EASE, useReducedMotion } from '@/lib/motion-presets';
import { DeleteIcon } from 'lucide-react';
import { motion } from 'motion/react';
import AuthPinButton from './auth-pin-button';
import { NUMPAD_KEYS, type NumpadKey } from './numpad-keys';
import { Spinner } from '@/components/ui/spinner';

type AuthPinPadProps = {
	disabled?: boolean;
	onDigit: (digit: string) => void;
	onDelete: () => void;
	isSubmitting?: boolean;
};

export default function AuthPinPad({ disabled, onDigit, onDelete, isSubmitting }: AuthPinPadProps) {
	const reducedMotion = useReducedMotion();

	function handleKey(key: NumpadKey) {
		if (!key || disabled) return;
		if (key === 'backspace') {
			onDelete();
			return;
		}
		onDigit(key);
	}

	return (
		<FadeIn delay={0.06} className='w-full'>
			<div className='relative rounded-2xl border border-white/8 bg-white/5 p-3 shadow-[0_0_24px_rgba(255,255,255,0.03)] backdrop-blur-sm'>
				<div className='flex flex-col gap-2'>
					{NUMPAD_KEYS.map((row, rowIndex) => (
						<div key={rowIndex} className='flex justify-center gap-2'>
							{row.map((key, colIndex) => {
								const flatIndex = rowIndex * 3 + colIndex;

								if (key === null) {
									return <div key={flatIndex} className='h-[60px] w-[60px]' />;
								}

								const button = (
									<AuthPinButton
										disabled={disabled}
										ariaLabel={key === 'backspace' ? 'Удалить' : `Цифра ${key}`}
										icon={key === 'backspace' ? <DeleteIcon size={22} /> : undefined}
										onClick={() => handleKey(key)}
									>
										{key !== 'backspace' ? key : null}
									</AuthPinButton>
								);

								if (reducedMotion) {
									return <div key={flatIndex}>{button}</div>;
								}

								return (
									<motion.div
										key={flatIndex}
										initial={{ opacity: 0, y: 10 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{
											duration: DURATION,
											delay: AUTH_STAGGER * flatIndex,
											ease: EASE,
										}}
									>
										{button}
									</motion.div>
								);
							})}
						</div>
					))}
				</div>
				{isSubmitting ? (
					<div className='absolute inset-0 flex items-center justify-center rounded-2xl bg-black/30 backdrop-blur-[1px]'>
						<Spinner className='h-8 w-8' />
					</div>
				) : null}
			</div>
		</FadeIn>
	);
}
